'use client'
// =============================================================================
// useRecentlyVisited — tracking d'usage réel pour les "favoris intelligents"
// (voir docs/architecture/command-center-ui.md, Sidebar v0.8 : "User behavior
// → Usage ranking → Sidebar suggestions"). N'existait pas avant (30 juillet
// 2026) — condition explicitement posée dans ce même document pour construire
// ce widget plutôt que des exemples statiques.
//
// localStorage, pas de backend : contrairement au token de chat (voir Slice 6,
// project_ui_shell memory), c'est une préférence de confort purement locale
// à l'appareil, jamais une donnée d'autorisation — aucun risque de
// fiabilité/sécurité à la garder côté client uniquement.
//
// Classement = fréquence (nombre de visites) puis récence en départage —
// jamais un score composite pondéré inventé (pas de poids "0.6×fréquence +
// 0.4×récence" sans donnée réelle pour calibrer ces poids). Capacité stockée
// (MAX_STORED) tronquée par récence — ce qui détermine CE QUI reste suivi ;
// l'ORDRE affiché est ensuite reclassé par fréquence, un problème distinct.
// =============================================================================
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

const STORAGE_KEY = 'ab_recent_nav'
const MAX_STORED = 10

interface RecentEntry { href: string; visitedAt: number; count: number }

function readStored(): RecentEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as RecentEntry[]) : []
  } catch { return [] }
}

function rank(entries: RecentEntry[]): string[] {
  return [...entries]
    .sort((a, b) => b.count - a.count || b.visitedAt - a.visitedAt)
    .map(e => e.href)
}

/** Retourne les chemins visités, classés par fréquence puis récence (pathname actuel inclus). */
export function useRecentlyVisited(): string[] {
  const pathname = usePathname()
  // Vide au premier rendu, serveur ET client : lire localStorage ici rendait un menu
  // différent côté client → erreur d'hydratation (« ACCÈS FRÉQUENT »). Rempli après montage.
  const [ranked, setRanked] = useState<string[]>([])

  useEffect(() => {
    if (!pathname) return
    const list = readStored()
    const idx = list.findIndex(e => e.href === pathname)
    if (idx >= 0) {
      list[idx] = { ...list[idx], visitedAt: Date.now(), count: list[idx].count + 1 }
    } else {
      list.push({ href: pathname, visitedAt: Date.now(), count: 1 })
    }
    // Capacité stockée : garder les MAX_STORED plus récemment touchées.
    const trimmed = list.sort((a, b) => b.visitedAt - a.visitedAt).slice(0, MAX_STORED)
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed)) } catch {}
    setRanked(rank(trimmed))
  }, [pathname])

  return ranked
}
